import {
  type MotionValue,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';
import React, { useRef } from 'react';

/**
 * How dim a word is before it is reached. Low enough to read as unlit, high
 * enough that the sentence is still legible as a shape, so the reader sees
 * what is coming rather than a blank.
 */
const UNREAD_OPACITY = 0.22;

/**
 * Where the reveal starts and finishes, as the block's own top passes down
 * the viewport. It completes well before the block reaches the middle of
 * the screen, so the text is fully lit by the time anyone settles on it.
 */
const REVEAL_OFFSET = ['start 0.92', 'start 0.42'] as const;

interface WordProps {
  children: string;
  progress: MotionValue<number>;
  /** Slice of the block's progress over which this word lights up */
  range: [number, number];
}

/**
 * ### Word
 *
 * One word, brightening across its own slice of the block's scroll. Its own
 * component because the opacity is a hook, and a hook cannot live in a loop.
 *
 * @returns The rendered word
 */
function Word({ children, progress, range }: WordProps) {
  const opacity = useTransform(progress, range, [UNREAD_OPACITY, 1]);

  return (
    <motion.span className="inline-block" style={{ opacity }}>
      {children}
    </motion.span>
  );
}

interface ScrollRevealTextProps {
  children: string;
  className?: string;
}

/**
 * ### ScrollRevealText
 *
 * A paragraph that lights up word by word as it is scrolled to, the way a
 * line is read rather than the way a block appears. The whole sentence is
 * always on the page; what changes is how much of it has been reached.
 *
 * The text is one string and the words are split here, so the copy modules
 * stay prose and nothing has to be written as an array to be animated. Under
 * a reduced-motion preference the paragraph is simply set in full, which is
 * also what a reader with scripting disabled and any crawler sees.
 *
 * @returns The rendered paragraph
 */
export default function ScrollRevealText({
  children,
  className,
}: ScrollRevealTextProps) {
  const container = useRef<HTMLParagraphElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: container,
    offset: [...REVEAL_OFFSET],
  });

  const words = children.split(' ');

  if (shouldReduceMotion) {
    return (
      <p ref={container} className={className}>
        {children}
      </p>
    );
  }

  return (
    <p ref={container} className={className}>
      {words.map((word, index) => (
        <React.Fragment key={`${word}-${index}`}>
          <Word
            progress={scrollYProgress}
            range={[index / words.length, (index + 1) / words.length]}
          >
            {word}
          </Word>
          {index < words.length - 1 ? ' ' : null}
        </React.Fragment>
      ))}
    </p>
  );
}
